import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Wifi, Umbrella, ChevronLeft, ChevronRight, LayoutGrid } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '../lib/supabase';
import { useAuth } from '../components/AuthProvider';
import { getSdrs, getSquads } from '../lib/api';
import { getUserAccessibleClients } from '../lib/multiClientUtils';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import UnifiedAgendaModal from '../components/agenda/UnifiedAgendaModal';
import BulkVacationModal from '../components/agenda/BulkVacationModal';
import WeeklyCalendarView from '../components/agenda/WeeklyCalendarView';
import PlanningView from '../components/planning/PlanningView';

interface Client {
  id: string;
  name: string;
  company_name?: string;
  start_date: string | null;
  end_date: string | null;
  days_per_month: number | null;
}

interface Squad {
  id: string;
  name: string;
  color: string | null;
}

interface Profile {
  user_id: string;
  sales_name: string | null;
  squad_id: string | null;
  email?: string | null;
  role?: string;
}

interface ScheduleDay {
  id: string;
  date: string;
  status: string;
  day_type: string;
  client_id: string | null;
  sdr_id: string | null;
  squad_id: string | null;
  slot?: string;
  work_location?: string | null;
  client?: Client;
  sdr?: Profile;
  squad?: Squad;
}

type TabType = 'agenda' | 'planning';

const AgendaPlanningPage: React.FC = () => {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState<Client[]>([]);
  const [squads, setSquads] = useState<Squad[]>([]);
  const [sdrs, setSdrs] = useState<Profile[]>([]);
  const [schedules, setSchedules] = useState<ScheduleDay[]>([]);

  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const isSuperAdmin = profile?.role === 'super_admin';
  const isAdmin = profile?.role === 'admin';
  const [activeTab, setActiveTab] = useState<TabType>(isSuperAdmin ? 'planning' : 'agenda');

  const [showModal, setShowModal] = useState(false);
  const [showVacationModal, setShowVacationModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleDay | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedSlot, setSelectedSlot] = useState<string | undefined>(undefined);
  const [preSelectedSdr, setPreSelectedSdr] = useState<string>('');
  const [preSelectedClient, setPreSelectedClient] = useState<string>('');

  const [recentlyUpdatedIds, setRecentlyUpdatedIds] = useState<Set<string>>(new Set());
  const [isConnected, setIsConnected] = useState(true);
  const [planningKey, setPlanningKey] = useState(0);

  const defaultSdrFilter = isAdmin && !isSuperAdmin ? profile.user_id : 'all';

  const markAsUpdated = useCallback((id: string) => {
    setRecentlyUpdatedIds(prev => new Set(prev).add(id));
    setTimeout(() => {
      setRecentlyUpdatedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }, 3000);
  }, []);

  useEffect(() => {
    loadData();
  }, [selectedMonth]);

  useEffect(() => {
    const startDate = format(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1), 'yyyy-MM-dd');
    const endDate = format(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0), 'yyyy-MM-dd');

    const scheduleChannel = supabase
      .channel('prospecting_schedule_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'prospecting_schedule',
          filter: `date=gte.${startDate},date=lte.${endDate}`
        },
        async (payload) => {
          if (payload.eventType === 'INSERT') {
            const { data } = await supabase
              .from('prospecting_schedule')
              .select(`
                *,
                client:clients(*),
                sdr:profiles!prospecting_schedule_sdr_id_fkey(*),
                squad:squads(*)
              `)
              .eq('id', payload.new.id)
              .single();

            if (data) {
              setSchedules(prev => [...prev, data]);
              markAsUpdated(data.id);
            }
          } else if (payload.eventType === 'UPDATE') {
            const { data } = await supabase
              .from('prospecting_schedule')
              .select(`
                *,
                client:clients(*),
                sdr:profiles!prospecting_schedule_sdr_id_fkey(*),
                squad:squads(*)
              `)
              .eq('id', payload.new.id)
              .single();

            if (data) {
              setSchedules(prev => prev.map(s => s.id === data.id ? data : s));
              markAsUpdated(data.id);
            }
          } else if (payload.eventType === 'DELETE') {
            setSchedules(prev => prev.filter(s => s.id !== payload.old.id));
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setIsConnected(true);
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          setIsConnected(false);
        }
      });

    const clientChannel = supabase
      .channel('clients_changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'clients'
        },
        (payload) => {
          loadClients();
        }
      )
      .subscribe();

    const profileChannel = supabase
      .channel('profiles_changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles'
        },
        (payload) => {
          loadSdrs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(scheduleChannel);
      supabase.removeChannel(clientChannel);
      supabase.removeChannel(profileChannel);
    };
  }, [selectedMonth, markAsUpdated]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadClients(),
        loadSquads(),
        loadSdrs(),
        loadSchedules()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    if (!profile) return;
    const accessibleClients = await getUserAccessibleClients(profile.user_id, profile.role);
    const activeClients = accessibleClients.filter((client: any) => client.statut === 'actif');
    if (activeClients) {
      setClients(activeClients);
    }
  };

  const loadSquads = async () => {
    try {
      const data = await getSquads();
      setSquads(data);
    } catch (error) {
      console.error('Error loading squads:', error);
    }
  };

  const loadSdrs = async () => {
    try {
      const data = await getSdrs();
      setSdrs(data);
    } catch (error) {
      console.error('Error loading SDRs:', error);
    }
  };

  const loadSchedules = async () => {
    const startDate = format(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1), 'yyyy-MM-dd');
    const endDate = format(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0), 'yyyy-MM-dd');

    const { data, error } = await supabase
      .from('prospecting_schedule')
      .select(`
        *,
        client:clients(*),
        sdr:profiles!prospecting_schedule_sdr_id_fkey(*),
        squad:squads(*)
      `)
      .gte('date', startDate)
      .lte('date', endDate);

    if (error) {
      console.error('Error loading schedules:', error);
    } else {
      setSchedules(data || []);
    }
  };

  const handleSlotClick = (date: Date, slot?: string, schedule?: ScheduleDay) => {
    setSelectedDate(date);
    setSelectedSlot(slot);
    setSelectedSchedule(schedule || null);
    setPreSelectedSdr(isAdmin && !isSuperAdmin ? profile.user_id : '');
    setPreSelectedClient('');
    setShowModal(true);
  };

  const handleCellClick = (sdrId: string, date: Date, schedule?: ScheduleDay) => {
    setSelectedDate(date);
    setPreSelectedSdr(sdrId);
    setSelectedSchedule(schedule || null);
    setPreSelectedClient('');
    setSelectedSlot(undefined);
    setShowModal(true);
  };


  const handleWorkLocationChange = useCallback(async (scheduleId: string, newLocation: string | null) => {
    setSchedules(prev => prev.map(s =>
      s.id === scheduleId ? { ...s, work_location: newLocation } : s
    ));

    const { error } = await supabase
      .from('prospecting_schedule')
      .update({ work_location: newLocation })
      .eq('id', scheduleId);

    if (error) {
      await loadSchedules();
    }
  }, []);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSchedule(null);
    setSelectedDate(undefined);
    setSelectedSlot(undefined);
    setPreSelectedSdr('');
    setPreSelectedClient('');
  };

  const handleSaveModal = async () => {
    await loadSchedules();
    setPlanningKey(prev => prev + 1);
    handleCloseModal();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="space-y-6">
        {activeTab === 'agenda' && (
          <div className="p-6">
            <div className="max-w-7xl mx-auto space-y-6">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-between"
              >
                <div className="flex items-center space-x-4">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">Agenda</h1>
                    <p className="text-gray-600 mt-1">
                      Vue calendrier de prospection
                    </p>
                  </div>
                  <AnimatePresence>
                    {isConnected && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="flex items-center space-x-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full"
                      >
                        <Wifi className="h-3.5 w-3.5 text-green-600" />
                        <span className="text-xs font-medium text-green-700">Synchronisé</span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <div className="flex items-center space-x-2">
                  {isSuperAdmin && (
                    <button
                      onClick={() => setShowVacationModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
                    >
                      <Umbrella className="h-5 w-5" />
                      Vacances globales
                    </button>
                  )}
                  <button
                    onClick={() => {
                      const newDate = new Date(selectedMonth);
                      newDate.setMonth(newDate.getMonth() - 1);
                      setSelectedMonth(newDate);
                    }}
                    className="p-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                    title="Mois précédent"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <select
                    value={selectedMonth.getMonth()}
                    onChange={(e) => {
                      const newDate = new Date(selectedMonth);
                      newDate.setMonth(parseInt(e.target.value));
                      setSelectedMonth(newDate);
                    }}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i} value={i}>
                        {format(new Date(2024, i, 1), 'MMMM')}
                      </option>
                    ))}
                  </select>
                  <select
                    value={selectedMonth.getFullYear()}
                    onChange={(e) => {
                      const newDate = new Date(selectedMonth);
                      newDate.setFullYear(parseInt(e.target.value));
                      setSelectedMonth(newDate);
                    }}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Array.from({ length: 5 }, (_, i) => {
                      const year = new Date().getFullYear() - 1 + i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                  <button
                    onClick={() => {
                      const newDate = new Date(selectedMonth);
                      newDate.setMonth(newDate.getMonth() + 1);
                      setSelectedMonth(newDate);
                    }}
                    className="p-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                    title="Mois suivant"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex space-x-2 bg-white p-1 rounded-lg border border-gray-200 overflow-x-auto shadow-sm"
              >
                <button
                  onClick={() => setActiveTab('agenda')}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
                    activeTab === 'agenda'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Calendar className="h-5 w-5" />
                  <span className="font-medium">Agenda</span>
                </button>
                <button
                  onClick={() => setActiveTab('planning')}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
                    activeTab === 'planning'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <LayoutGrid className="h-5 w-5" />
                  <span className="font-medium">Planning</span>
                </button>
              </motion.div>

              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <WeeklyCalendarView
                  schedules={schedules}
                  selectedMonth={selectedMonth}
                  clients={clients}
                  sdrs={sdrs}
                  squads={squads}
                  onSlotClick={isSuperAdmin ? handleSlotClick : undefined}
                  onWorkLocationChange={isSuperAdmin ? handleWorkLocationChange : undefined}
                  recentlyUpdatedIds={recentlyUpdatedIds}
                  defaultSdrFilter={defaultSdrFilter}
                />
              </motion.div>
            </div>
          </div>
        )}

        {activeTab === 'planning' && (
          <div className="p-6">
            <div className="max-w-7xl mx-auto space-y-6">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex space-x-2 bg-white p-1 rounded-lg border border-gray-200 overflow-x-auto shadow-sm"
              >
                <button
                  onClick={() => setActiveTab('agenda')}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                >
                  <Calendar className="h-5 w-5" />
                  <span className="font-medium">Agenda</span>
                </button>
                <button
                  onClick={() => setActiveTab('planning')}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap bg-blue-600 text-white shadow-lg"
                >
                  <LayoutGrid className="h-5 w-5" />
                  <span className="font-medium">Planning</span>
                </button>
              </motion.div>

              <PlanningView
                key={planningKey}
                initialMonth={selectedMonth}
                sdrs={sdrs}
                squads={squads}
                clients={clients}
                onAddDay={() => setShowModal(true)}
                onCellClick={handleCellClick}
              />
            </div>
          </div>
        )}
      </div>

      {showModal && isSuperAdmin && (
        <UnifiedAgendaModal
          mode={selectedSchedule ? 'edit' : 'create'}
          initialSchedule={selectedSchedule}
          initialDate={selectedDate ? format(selectedDate, 'yyyy-MM-dd') : undefined}
          clients={clients}
          sdrs={sdrs}
          squads={squads}
          schedules={schedules}
          preSelectedSdr={preSelectedSdr}
          preSelectedClient={preSelectedClient}
          onClose={handleCloseModal}
          onSave={handleSaveModal}
        />
      )}

      {showVacationModal && isSuperAdmin && (
        <BulkVacationModal
          sdrs={sdrs}
          onClose={() => setShowVacationModal(false)}
          onSave={async () => {
            await loadSchedules();
            setPlanningKey(prev => prev + 1);
            setShowVacationModal(false);
          }}
        />
      )}
    </div>
  );
};

export default AgendaPlanningPage;
